import { isContentfulString, isEmptyArrayOrObject } from '@philips-internal/oswald-ui-utils';
const BASE_PATTERN = /^[a-z]{2}$/;
const LANGUAGE_PATTERN = /^[a-z]{2}-[a-z]{2}$/;
const SERVICE_NOT_INITIALIZED_MESSAGE = 'Service has not been initialized yet.';
/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/language
 */
function getBrowserLanguage() {
    return normalizeLanguage(navigator.language);
}
/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/languages
 */
function getBrowserLanguages() {
    return navigator.languages.map(normalizeLanguage);
}
function getChosenLanguage(defaultLanguage, language, supportedLanguages) {
    const [baseLanguage] = language.split('-');
    if (supportedLanguages.includes(language)) {
        return language;
    }
    if (supportedLanguages.includes(baseLanguage)) {
        return language;
    }
    return defaultLanguage;
}
function isFollowingLanguagePattern(language) {
    return BASE_PATTERN.test(language) || LANGUAGE_PATTERN.test(language);
}
function languageError(message) {
    return new Error(`[Language Service]: ${message}`);
}
function normalizeLanguage(language) {
    return language.toLowerCase();
}
function createLanguageService() {
    let currentLanguage;
    let defaultLanguage;
    let initialized = false;
    let supportedLanguages;
    const listeners = new Set();
    const browserLanguage = getBrowserLanguage();
    currentLanguage = browserLanguage;
    defaultLanguage = browserLanguage;
    const addListener = (fn) => {
        if (!initialized) {
            throw languageError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        if (listeners.has(fn)) {
            throw languageError('');
        }
        listeners.add(fn);
        return () => {
            listeners.delete(fn);
        };
    };
    const getCurrentLanguage = () => {
        return currentLanguage;
    };
    const getDefaultLanguage = () => {
        return defaultLanguage;
    };
    const getSupportedLanguages = () => {
        if (!initialized) {
            throw languageError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        return supportedLanguages;
    };
    const hasBeenInitialized = () => {
        return initialized;
    };
    const initialize = async () => {
        if (initialized) {
            throw languageError('Service already initialized.');
        }
        // @ts-expect-error
        const { get } = await import('virtual:oswald-configuration');
        const runtime = await get();
        const configuration = runtime.i18n.languages;
        if (!isContentfulString(configuration.defaultLanguage)) {
            throw languageError(`A default language is required.`);
        }
        if (!isFollowingLanguagePattern(configuration.defaultLanguage)) {
            throw languageError(`The default language is not following any recognizable pattern (${BASE_PATTERN} or ${LANGUAGE_PATTERN}).`);
        }
        if (isEmptyArrayOrObject(configuration.supportedLanguages)) {
            throw languageError(`A list of supported languages is required.`);
        }
        if (!configuration.supportedLanguages.some(supported => isFollowingLanguagePattern(supported))) {
            throw languageError(`One or more of the supported languages is not following any recognizable pattern (${BASE_PATTERN} or ${LANGUAGE_PATTERN}).`);
        }
        if (!configuration.supportedLanguages.some(supported => supported === configuration.defaultLanguage)) {
            throw languageError(`The default language is not present in the list of supported languages.`);
        }
        defaultLanguage = configuration.defaultLanguage;
        supportedLanguages = configuration.supportedLanguages;
        initialized = true;
        let success = false;
        for (const browserLanguage of getBrowserLanguages()) {
            try {
                setCurrentLanguage(browserLanguage);
                success = true;
                break;
            }
            catch {
                continue;
            }
        }
        if (!success) {
            throw languageError('Unable to find a supported language in the list of preferred languages.');
        }
    };
    const isLanguageSupported = (language) => {
        if (!initialized) {
            throw languageError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        return supportedLanguages.includes(language);
    };
    const setCurrentLanguage = (language) => {
        if (!initialized) {
            throw languageError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        language = language.toLowerCase();
        if (!isFollowingLanguagePattern(language)) {
            throw languageError(`Language ${language} is not following any recognizable pattern (${BASE_PATTERN} or ${LANGUAGE_PATTERN}).`);
        }
        currentLanguage = getChosenLanguage(defaultLanguage, language, supportedLanguages);
        notifyListeners();
    };
    function notifyListeners() {
        for (const listener of listeners) {
            listener(currentLanguage);
        }
    }
    return {
        addListener,
        getCurrentLanguage,
        getDefaultLanguage,
        getSupportedLanguages,
        hasBeenInitialized,
        initialize,
        isLanguageSupported,
        setCurrentLanguage,
    };
}
const LanguageService = createLanguageService();
export { LanguageService, };
//# sourceMappingURL=language-service.js.map